/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { TrecApiError, TrecError } from "@trec/api/errors";
import { Meeting, VideoChatService } from "@trec/api/video-chat";
import { ErrorAction } from "@trec/components/store";
import { throwError } from "rxjs";
import { catchError, switchMap, switchMapTo, tap } from "rxjs/operators";
import { VideoMeetingActions } from "./video-meeting.actions";

/**
 * Ngxs messages state.
 */
export interface VideoMeetingStateModel {
  /**
   * Meetings created by me.
   */
  meetings: Meeting[];

  /**
   * Current meeting.
   */
  currentMeeting: Meeting | null;

  /**
   * Flag indicating that operation has failed.
   */
  error: TrecError | TrecApiError | null;
}

@State<VideoMeetingStateModel>({
  name: "videoMeeting",
  defaults: {
    meetings: [],
    currentMeeting: null,

    error: null,
  },
})
@Injectable()
export class VideoMeetingState {
  constructor(private videoChat: VideoChatService) {}

  // Selectors ////////////////////////////////////////////////////////////////////////////////////

  @Selector() static meetings(state: VideoMeetingStateModel) {
    return state.meetings;
  }

  @Selector() static currentMeeting(state: VideoMeetingStateModel) {
    return state.currentMeeting;
  }

  // Select status //////////////////////////////

  @Selector() static error(state: VideoMeetingStateModel) {
    return state.error;
  }

  // Actions //////////////////////////////////////////////////////////////////////////////////////

  // //////////////////////////////////////////////////////////////////////////////////////////////
  //  Errors
  // //////////////////////////////////////////////////////////////////////////////////////////////

  @Action(VideoMeetingActions.Error)
  fetchErrors(ctx: StateContext<VideoMeetingStateModel>, { payload }: ErrorAction) {
    ctx.patchState({ error: payload });
  }

  @Action(VideoMeetingActions.FetchMeetings)
  fetchMeetings(
    ctx: StateContext<VideoMeetingStateModel>,
    _action: VideoMeetingActions.FetchMeetings,
  ) {
    return this.videoChat
      .getMeetingsV3ICanParticipate({
        withPersonalData: false,
        idStruttura: _action?.payload.idStruttura,
        isPhysician: _action?.payload.isPhysician,
        idTrecUser: _action?.payload.idUserTrec,
        skipCreatorPersonalData: _action?.payload.skipCreatorPersonalData,
        idPaziente: _action?.payload.idPaziente,
        history: _action?.payload.history,
      })
      .pipe(
        switchMap((meetings) => ctx.dispatch(new VideoMeetingActions.SetMeetings({ meetings }))),
        catchError((err) => ctx.dispatch(new VideoMeetingActions.Error(err))),
      );
  }

  @Action(VideoMeetingActions.SetMeetings)
  setMeetings(
    ctx: StateContext<VideoMeetingStateModel>,
    { payload: { meetings } }: VideoMeetingActions.SetMeetings,
  ) {
    ctx.patchState({ meetings, error: null });
  }

  @Action(VideoMeetingActions.CreateMeeting)
  createMeeting(
    ctx: StateContext<VideoMeetingStateModel>,
    {
      payload: { meeting, idUserTrec, isPhysician, idStruttura, idPaziente },
    }: VideoMeetingActions.CreateMeeting,
  ) {
    return this.videoChat.createMeetingV3(meeting).pipe(
      // Re-fetch all meeting list
      switchMap(() =>
        ctx.dispatch(
          new VideoMeetingActions.FetchMeetings({
            isPhysician: idPaziente ? false : isPhysician,
            idUserTrec,
            idStruttura,
            skipCreatorPersonalData: true,
            idPaziente: idPaziente ? idPaziente : null,
            history: idPaziente ? true : false,
          }),
        ),
      ),
      catchError((err) =>
        ctx.dispatch(new VideoMeetingActions.Error(err)).pipe(switchMapTo(throwError(err))),
      ),
    );
  }

  @Action(VideoMeetingActions.CreateDirectMeeting)
  createDirectMeeting(
    ctx: StateContext<VideoMeetingStateModel>,
    { payload: { patientId, idStruttura, patientName } }: VideoMeetingActions.CreateDirectMeeting,
  ) {
    return this.videoChat
      .createMeetingV3({
        participants: [{ trecUserId: patientId, isPatient: true }],
        patientName: patientName,
        description: "Chiamata diretta",
        duration: 120, // Ignored
        startTime: new Date().toISOString(), // Ignored
        direct: true,
        medicalOfficeData: {
          medicalOfficeCode: idStruttura,
          medicalOfficeDescription: null,
          organizationalUnitCode: null,
          organizationalUnitDescription: null,
        },
      })
      .pipe(
        // Set current meeting
        switchMap((meeting) =>
          ctx.dispatch(new VideoMeetingActions.SetCurrentMeeting({ meeting })),
        ),
        catchError((err) =>
          ctx.dispatch(new VideoMeetingActions.Error(err)).pipe(switchMapTo(throwError(err))),
        ),
      );
  }

  @Action(VideoMeetingActions.StartMeeting)
  startMeeting(
    ctx: StateContext<VideoMeetingStateModel>,
    { payload: { meetingId } }: VideoMeetingActions.StartMeeting,
  ) {
    return this.videoChat.startMeeting(meetingId).pipe(
      // Do nothing
      catchError((err) => ctx.dispatch(new VideoMeetingActions.Error(err))),
    );
  }

  @Action(VideoMeetingActions.EndMeeting)
  endMeeting(
    ctx: StateContext<VideoMeetingStateModel>,
    { payload: { meetingId } }: VideoMeetingActions.EndMeeting,
  ) {
    return this.videoChat.endMeeting(meetingId).pipe(
      // Do nothing
      catchError((err) => ctx.dispatch(new VideoMeetingActions.Error(err))),
    );
  }

  @Action(VideoMeetingActions.FetchCurrentMeeting)
  fetchCurrentMeeting(
    ctx: StateContext<VideoMeetingStateModel>,
    { payload: { meetingId } }: VideoMeetingActions.FetchCurrentMeeting,
  ) {
    return this.videoChat.getMeeting(meetingId, { withPersonalData: true }).pipe(
      switchMap((meeting) => ctx.dispatch(new VideoMeetingActions.SetCurrentMeeting({ meeting }))),
      catchError((err) => ctx.dispatch(new VideoMeetingActions.Error(err))),
    );
  }

  @Action(VideoMeetingActions.SetCurrentMeeting)
  setCurrentMeeting(
    ctx: StateContext<VideoMeetingStateModel>,
    { payload: { meeting } }: VideoMeetingActions.SetCurrentMeeting,
  ) {
    ctx.patchState({ currentMeeting: meeting, error: null });
  }

  @Action(VideoMeetingActions.CancelMeeting)
  cancelMeeting(
    ctx: StateContext<VideoMeetingStateModel>,
    { payload: { meetingId } }: VideoMeetingActions.CancelMeeting,
  ) {
    return this.videoChat.cancelMeeting(meetingId).pipe(
      tap((cancelledMeeting) => {
        // Update meeting in store (and add personal data taken from the current meeting object)
        ctx.patchState({
          meetings: ctx
            .getState()
            .meetings.map((m) =>
              m.id !== meetingId
                ? m
                : cancelledMeeting.withPersonalData((m as any)._personalData ?? {}),
            ),
          error: null,
        });
      }),
      catchError((err) =>
        ctx.dispatch(new VideoMeetingActions.Error(err)).pipe(switchMapTo(throwError(err))),
      ),
    );
  }
}
